import { type Block, type Change, generateKey, isParentPathEqual, Schema } from '@gitbook/slate';

import {
    getConflictCounterpartKey,
    getConflictKeyOrigin,
    getOriginalKey,
    hasConflictData,
    isConflictKey,
} from '../conflicts';

/**
 * Schema when editing a document with conflicts
 */
export const schemaForConflicts = Schema.create({
    validations: [
        /*
         * Rule to set the schemaType to "conflicts" on the document.
         */
        function noConflictSchemaType(node) {
            if (node.object !== 'document' || node.data.get('schemaType') === 'conflicts') {
                return null;
            }

            return (change) => {
                change.setDataByKey(node.key, ['schemaType'], 'conflicts');
            };
        },
        /*
         * Rule to ensure that a conflict has its alternatives in the document.
         * Otherwise the block is marked as "normal".
         */
        function noInvalidConflicts(document) {
            // Conflicts are being managed only at the top level
            if (document.object !== 'document') {
                return null;
            }

            const missingConflictKey: Block[] = [];
            const missingConflictData: Block[] = [];
            const missingCounterpart: Block[] = [];
            document.nodes.forEach((block) => {
                const blockHasConflictKey = isConflictKey(block.key);
                const blockHasConflictData = hasConflictData(block);
                if (!blockHasConflictKey && !blockHasConflictData) {
                    // Not a trace of conflict
                    return;
                }

                if (!blockHasConflictKey) {
                    missingConflictKey.push(block);
                    return;
                }

                if (!blockHasConflictData) {
                    missingConflictData.push(block);
                    return;
                }

                const counterpartKey = getConflictCounterpartKey(block.key);
                const hasCounterpart = document.getChild(counterpartKey);
                if (!hasCounterpart) {
                    missingCounterpart.push(block);
                }
            });

            if (
                missingConflictKey.length === 0 &&
                missingConflictData.length === 0 &&
                missingCounterpart.length === 0
            ) {
                return null;
            }

            return (change) => {
                missingConflictKey.forEach((n) => {
                    change.setNodeByKey(
                        n.key,
                        {
                            data: n.data.delete('conflict'),
                        },
                        { normalize: false }
                    );
                });
                missingConflictData.forEach((n) => {
                    change.removeNodeByKey(n.key, { normalize: false });
                });
                missingCounterpart.forEach((n) => {
                    cleanConflict(change, n);
                });
            };
        },
    ],
});

/**
 * Schema to remove conflicts data.
 */
export const schemaToStripConflicts = Schema.create({
    validations: [
        /*
         * Rule to remove the schemaType "conflicts" from the document.
         * This data is set during the mergeDocuments.
         */
        function noConflictSchemaType(node) {
            if (node.object !== 'document' || node.data.get('schemaType') !== 'conflicts') {
                return null;
            }

            return (change) => {
                change.removeDataByKey(node.key, ['schemaType']);
            };
        },
        /*
         * Rule to ensure that there are no conflicts in normal mode.
         * It removes everything related to base and cleanup keys for head nodes.
         */
        function noConflicts(node) {
            if (!isConflictKey(node.key)) {
                return null;
            }

            return (change) => {
                const origin = getConflictKeyOrigin(node.key);
                const originKey = getOriginalKey(node.key);

                if (origin === 'base') {
                    const originNodePath =
                        change.value.document.getPath(originKey) ??
                        change.value.document.getPath(getConflictCounterpartKey(node.key)) ??
                        // Because of the bug introduced in https://github.com/GitbookIO/gitbook-x/pull/19394
                        // We need to check the invalid keys as well
                        change.value.document.getPath(getOriginalKey(node.key, false)) ??
                        change.value.document.getPath(getConflictCounterpartKey(node.key, false));

                    if (originNodePath) {
                        const nodePath = change.value.document.getPath(node.key);
                        if (nodePath && isParentPathEqual(nodePath, originNodePath)) {
                            // Delete the base if the head node is present or will be created
                            // in the same parent
                            change.removeNodeByKey(node.key);
                            return;
                        }
                    }
                }

                // Randomize the key if the original node exists in another parent
                const existingOriginalNode = change.value.document.getNode(originKey);

                const cleanedNode = node.merge({
                    key: existingOriginalNode ? generateKey() : originKey,
                    ...('data' in node && node.data
                        ? {
                              data: node.data.delete('conflict'),
                          }
                        : {}),
                });

                // We need to use replaceNodeByKey in order to change a block key
                return change.replaceNodeByKey(node.key, cleanedNode);
            };
        },
    ],
});

/**
 * Clean conflict on a block and reset it to normal state (including all its descendant).
 */
function cleanConflict(change: Change, block: Block) {
    const processedKeys = new Set<string>();
    const cleanedBlock = block
        .merge({
            key: getOriginalKey(block.key),
            data: block.data.delete('conflict'),
        })
        .mapDescendants((node) => {
            if (!isConflictKey(node.key)) {
                return node;
            }

            const originalKey = getOriginalKey(node.key);
            const existingNodeExists =
                Boolean(change.value.document.getPath(originalKey)) ||
                processedKeys.has(originalKey);

            // We also add it to the processed keys to avoid setting the same key for 2 different nodes
            // before the actual change is applied
            processedKeys.add(originalKey);
            return node.merge({
                key: existingNodeExists ? generateKey() : originalKey,
            });
        });

    // Update the selection if needed
    const { selection } = change.value;
    const newSelection = selection.merge({
        // @ts-expect-error
        anchorKey: selection.anchorKey === block.key ? cleanedBlock.key : selection.anchorKey,
        // @ts-expect-error
        focusKey: selection.focusKey === block.key ? cleanedBlock.key : selection.focusKey,
    });

    change.replaceNodeByKey(block.key, cleanedBlock);
    change.select(newSelection);
}
